import type { AxiosError } from 'axios';
import { CanceledError } from 'axios';
import { useToast } from '../../components/CustomToast';

export function isCancelledRequest(error: unknown): boolean {
  if (error instanceof CanceledError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const code = 'code' in error ? String((error as { code?: unknown }).code) : '';
  if (code === 'ERR_CANCELED') return true;
  const name = 'name' in error ? String((error as { name?: unknown }).name) : '';
  return name === 'AbortError';
}

// Simple type for API errors
export interface ApiError extends AxiosError {
  response?: AxiosError['response'] & {
    data?: {
      message?: string;
      detail?: string;
      errors?: Array<{
        code?: string;
        message?: string;
        fields?: Record<string, string[]>;
        location?: string;
        details?: unknown;
        field?: string; // legacy format support
      }>;
    };
  };
}

// Custom error handlers for specific APIs
type CustomErrorHandler = (error: ApiError) => boolean; // return true if handled

const customHandlers: CustomErrorHandler[] = [];

/**
 * Extract error message from backend response
 */
function getErrorMessage(error: ApiError): string {
  const data = error.response?.data;

  // Check if errors array exists with new schema (validation errors with fields)
  if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];

    // Handle field validation errors
    if (firstError?.fields && Object.keys(firstError.fields).length > 0) {
      const fieldErrors: string[] = [];
      for (const messages of Object.values(firstError.fields)) {
        if (Array.isArray(messages) && messages.length > 0) {
          fieldErrors.push(messages.join(', '));
        }
      }
      if (fieldErrors.length > 0) {
        return fieldErrors.join('\n');
      }
    }

    // If no field errors, return the error message
    if (firstError?.message) return firstError.message;
  }

  // Handle simple message fields (legacy format)
  if (data?.message) return data.message;
  if (data?.detail) return data.detail;

  // Fallback to axios error message
  if (error.message) return error.message;

  return 'خطایی رخ داده است. لطفا دوباره تلاش کنید.';
}

/**
 * Get error title based on status code
 */
// function getErrorTitle(error: ApiError): string {
//   const status = error.response?.status;

//   switch (status) {
//     case 400:
//       return 'Bad Request';
//     case 401:
//       return 'Unauthorized';
//     case 403:
//       return 'Forbidden';
//     case 404:
//       return 'Not Found';
//     case 409:
//       return 'Conflict';
//     case 422:
//       return 'Validation Error';
//     case 500:
//       return 'Server Error';
//     default:
//       return 'خطا';
//   }
// }

/**
 * Global error handler
 */
export function useApiError() {
  const { showToast } = useToast();

  const handleError = (error: unknown) => {
    if (isCancelledRequest(error)) {
      return;
    }

    const apiError = error as ApiError;

    // Don't show toast for 401 errors (they trigger logout)
    if (apiError.response?.status === 401) {
      return;
    }

    // Don't show toast for 404 (not found) - treat as empty/no results, not an error to display
    if (apiError.response?.status === 404) {
      return;
    }

    // Check custom handlers first
    for (const handler of customHandlers) {
      if (handler(apiError)) {
        return; // Custom handler took care of it
      }
    }

    // 413 Request Entity Too Large - show user-friendly Persian message
    if (apiError.response?.status === 413) {
      showToast({
        variant: 'error',
        message: 'حجم فایل بیش از حد مجاز است.',
        duration: 5000,
        allowMultiple: true,
      });
      return;
    }

    // Default handling - show toast with backend message
    const message = getErrorMessage(apiError);
    // const title = getErrorTitle(error);

    showToast({
      variant: 'error',
      // title,
      message,
      duration: 5000,
      allowMultiple: true,
    });
  };

  return { handleError };
}

/**
 * Register a custom error handler
 */
export function addCustomErrorHandler(handler: CustomErrorHandler) {
  customHandlers.push(handler);
}

/**
 * Example: Add custom handler for specific status codes
 */
export function addStatusCodeHandler(statusCode: number, handler: (error: ApiError) => void) {
  addCustomErrorHandler((error: ApiError) => {
    if (error.response?.status === statusCode) {
      handler(error);
      return true; // Handled
    }
    return false;
  });
}
